import { useEffect, useState } from "react";
import { adminApi } from "../lib/api";
import { useCMS } from "../lib/cms";
import AdminOrders from "../components/admin/AdminOrders";
import AdminSellRequests from "../components/admin/AdminSellRequests";
import AdminArticles from "../components/admin/AdminArticles";
import { groupKeys } from "../components/admin/contentSections";
import { toast } from "sonner";
import { RefreshCw, Send, Save, RotateCcw, Download, Trash2, Eye, EyeOff, Search } from "lucide-react";
import { gmailCompose } from "../lib/gmail";

export default function Admin() {
  const cms = useCMS();
  const [user, setUser] = useState(sessionStorage.getItem("vmr_admin_user") || "");
  const [pass, setPass] = useState(sessionStorage.getItem("vmr_admin_pass") || "");
  const [authed, setAuthed] = useState(false);
  const [status, setStatus] = useState(null);
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [content, setContent] = useState(null);
  const [clicks, setClicks] = useState(null);
  const [messages, setMessages] = useState(null);
  const [orders, setOrders] = useState(null);
  const [sellRequests, setSellRequests] = useState(null);
  const [listings, setListings] = useState(null);
  const [listingsQ, setListingsQ] = useState("");
  const [listingsStatus, setListingsStatus] = useState("active");
  const [listingsPage, setListingsPage] = useState(1);
  const [tab, setTab] = useState("status");
  const [syncing, setSyncing] = useState(false);

  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [tiers, setTiers] = useState({ free: true, early: true, curator: true });

  const client = adminApi(user, pass);

  const login = async (e) => {
    e?.preventDefault();
    try {
      await client.post("/admin/login");
      sessionStorage.setItem("vmr_admin_user", user);
      sessionStorage.setItem("vmr_admin_pass", pass);
      setAuthed(true);
      await refresh();
    } catch {
      toast.error("Credenciales incorrectas");
    }
  };

  const logout = () => {
    sessionStorage.removeItem("vmr_admin_user");
    sessionStorage.removeItem("vmr_admin_pass");
    setAuthed(false);
    setUser(""); setPass("");
  };

  const refresh = async () => {
    try {
      const [s, subs, camps, cont, clk, msgs] = await Promise.all([
        client.get("/admin/sync/status"),
        client.get("/admin/subscribers"),
        client.get("/admin/campaigns"),
        client.get("/admin/content"),
        client.get("/admin/clicks"),
        client.get("/admin/messages"),
      ]);
      setStatus(s.data);
      setSubscribers(subs.data.items || []);
      setCampaigns(camps.data.items || []);
      setContent(cont.data);
      setClicks(clk.data);
      setMessages(msgs.data);
    } catch (e) {
      toast.error("No se pudo cargar el panel");
    }
  };

  const loadOrders = async () => {
    try {
      const { data } = await client.get("/admin/orders");
      setOrders(data);
    } catch {
      toast.error("No se pudieron cargar los pedidos");
    }
  };

  const loadSellRequests = async () => {
    try {
      const { data } = await client.get("/admin/sell-requests");
      setSellRequests(data);
    } catch {
      toast.error("No se pudieron cargar las ofertas");
    }
  };

  const loadListings = async (q = listingsQ, status = listingsStatus, page = listingsPage) => {
    try {
      const params = { page, per_page: 30 };
      if (q) params.q = q;
      if (status) params.status = status;
      const { data } = await client.get("/admin/listings", { params });
      setListings(data);
    } catch {
      toast.error("Error al cargar el catálogo");
    }
  };

  useEffect(() => {
    if (authed && tab === "catalog") loadListings();
    if (authed && tab === "orders") loadOrders();
    if (authed && tab === "sell") loadSellRequests();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed, tab, listingsStatus, listingsPage]);

  const saveBlock = async (key, es, en) => {
    try {
      await client.put(`/admin/content/${encodeURIComponent(key)}`, { es, en });
      toast.success(`Guardado: ${key}`);
      await refresh();
      await cms.refresh();
    } catch {
      toast.error("Error al guardar");
    }
  };

  const resetBlock = async (key) => {
    try {
      await client.post(`/admin/content/${encodeURIComponent(key)}/reset`);
      toast.success(`Restaurado: ${key}`);
      await refresh();
      await cms.refresh();
    } catch {
      toast.error("Error al restaurar");
    }
  };

  useEffect(() => {
    if (user && pass) {
      login();
    }
    // eslint-disable-next-line
  }, []);

  // The sync runs in GitHub Actions and takes ~30-90s, so after starting it we poll the
  // status until a run newer than the click has finished, then report its real outcome.
  const triggerSync = async () => {
    setSyncing(true);
    try {
      const { data } = await client.post("/admin/sync");
      if (!data.started) {
        toast.info(
          data.since ? `Ya hay un sync en marcha (desde ${fmtDate(data.since)}).` : "Ya hay un sync en marcha.",
          { duration: 8000 },
        );
        await refresh();
        return;
      }
      if (data.via !== "github_actions") {
        // Local development: the sync already ran inside the request.
        toast.success(`Sync terminado: ${data.items_total ?? 0} discos`);
        await refresh();
        return;
      }
      toast.message("Sync lanzado. Tarda alrededor de un minuto…");
      const requestedAt = Date.parse(data.requested_at || new Date().toISOString());
      const deadline = Date.now() + 4 * 60 * 1000;
      while (Date.now() < deadline) {
        await new Promise((r) => setTimeout(r, 5000));
        const { data: st } = await client.get("/admin/sync/status");
        setStatus(st);
        const last = st.last_sync;
        // Small margin for clock skew between the Worker and the browser.
        if (last && Date.parse(last.started_at) >= requestedAt - 5000 && last.status !== "running") {
          if (last.status === "success") {
            toast.success(`Sync correcto: ${last.items_new} nuevos, ${last.items_updated} actualizados, ${last.items_archived} archivados`, { duration: 10000 });
          } else {
            toast.error(`El sync ha fallado: ${(last.errors || [])[0] || "sin detalle"}`, { duration: 15000 });
          }
          await refresh();
          return;
        }
      }
      toast.error("El sync no ha terminado en 4 minutos. Revisa GitHub → Actions → Discogs sync.", { duration: 15000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo iniciar el sync", { duration: 15000 });
    } finally {
      setSyncing(false);
    }
  };

  const sendCampaign = async () => {
    if (!subject.trim() || !body.trim()) {
      toast.error("Asunto y cuerpo son obligatorios"); return;
    }
    const selectedTiers = Object.entries(tiers).filter(([_, v]) => v).map(([k]) => k);
    if (!selectedTiers.length) { toast.error("Selecciona al menos un tier"); return; }
    try {
      const { data } = await client.post("/admin/campaigns", {
        subject, body_markdown: body, tiers: selectedTiers,
      });
      toast.success(`Enviando a ${data.recipients} suscriptores`);
      setSubject(""); setBody("");
      setTimeout(refresh, 2000);
    } catch {
      toast.error("Error al enviar la campaña");
    }
  };

  const deleteMessage = async (id) => {
    if (!confirm("¿Eliminar este mensaje?")) return;
    try {
      await client.delete(`/admin/messages/${id}`);
      toast.success("Mensaje eliminado");
      await refresh();
    } catch {
      toast.error("Error al eliminar");
    }
  };

  const toggleListing = async (listing_id, web_sold) => {
    try {
      await client.put(`/admin/listings/${listing_id}`, { web_sold });
      toast.success(web_sold ? "Disco ocultado" : "Disco restaurado");
      await loadListings();
      await refresh();
    } catch {
      toast.error("Error al actualizar");
    }
  };

  const exportCSV = async (kind) => {
    try {
      const res = await client.get(`/admin/export/${kind}.csv`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${kind}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("CSV descargado");
    } catch {
      toast.error("Error al exportar");
    }
  };

  if (!authed) {
    return (
      <div className="max-w-md mx-auto px-5 pt-32 pb-32" data-testid="admin-login-page">
        <h1 className="text-4xl mb-2">Admin</h1>
        <p className="vmr-tiny text-muted-foreground mb-10">Acceso restringido</p>
        <form onSubmit={login} className="flex flex-col gap-5">
          <input
            value={user}
            onChange={(e) => setUser(e.target.value)}
            placeholder="Usuario"
            className="bg-transparent border border-border px-3 py-3 focus:outline-none focus:border-foreground"
            data-testid="admin-user"
            required
          />
          <input
            type="password"
            value={pass}
            onChange={(e) => setPass(e.target.value)}
            placeholder="Contraseña"
            className="bg-transparent border border-border px-3 py-3 focus:outline-none focus:border-foreground"
            data-testid="admin-pass"
            required
          />
          <button
            type="submit"
            className="bg-foreground text-background py-3 vmr-tiny tracking-widest"
            data-testid="admin-login-submit"
          >
            Entrar
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 pt-12 md:pt-16" data-testid="admin-page">
      <div className="flex items-end justify-between border-b border-border pb-6 mb-10">
        <div>
          <p className="vmr-tiny text-muted-foreground mb-2">Panel</p>
          <h1 className="text-4xl md:text-5xl">Admin · Maggie Records</h1>
        </div>
        <button onClick={logout} className="vmr-tiny text-muted-foreground hover:text-foreground" data-testid="admin-logout">
          Salir
        </button>
      </div>

      <nav className="flex flex-wrap gap-x-6 gap-y-2 vmr-tiny mb-10 border-b border-border" data-testid="admin-tabs">
        {[
          { k: "status", l: "Estado" },
          { k: "orders", l: `Pedidos${status?.new_orders ? ` (${status.new_orders} nuevos)` : ""}` },
          { k: "sell", l: `Compramos${status?.new_sell_requests ? ` (${status.new_sell_requests})` : ""}` },
          { k: "articles", l: "Artículos" },
          { k: "catalog", l: "Catálogo" },
          { k: "subscribers", l: `Suscriptores (${subscribers.length})` },
          { k: "messages", l: `Mensajes${messages?.count ? ` (${messages.count})` : ""}` },
          { k: "campaigns", l: "Campañas" },
          { k: "content", l: "Textos" },
          { k: "clicks", l: `Clicks${clicks?.total ? ` (${clicks.total})` : ""}` },
        ].map((tabDef) => (
          <button
            key={tabDef.k}
            onClick={() => setTab(tabDef.k)}
            className={`pb-3 -mb-px border-b-2 ${tab === tabDef.k ? "border-[hsl(var(--accent))] text-[hsl(var(--accent))]" : "border-transparent text-muted-foreground"}`}
            data-testid={`admin-tab-${tabDef.k}`}
          >
            {tabDef.l}
          </button>
        ))}
      </nav>

      {tab === "status" && (
        <div className="space-y-8" data-testid="admin-status-tab">
          {/* Métricas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card label="Listings activos" value={status?.total_listings ?? "—"} />
            <Card label="Clicks a Discogs" value={clicks?.total ?? 0} sub="botón 'Comprar en Discogs'" />
            <Card label="Suscriptores" value={status?.total_subscribers ?? "—"} />
            <Card label="Mensajes" value={messages?.count ?? 0} sub="formulario de contacto" />
          </div>

          {/* Sync */}
          <div className="border border-border p-6 md:p-8">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl">Último sync con Discogs</h2>
              <button
                onClick={triggerSync}
                disabled={syncing || status?.running}
                className="inline-flex items-center gap-2 vmr-tiny bg-[hsl(var(--accent))] text-white px-4 py-2 disabled:opacity-50"
                data-testid="admin-sync-now"
              >
                <RefreshCw size={12} className={syncing || status?.running ? "animate-spin" : ""} />
                {syncing || status?.running ? "Sincronizando…" : "Sincronizar ahora"}
              </button>
            </div>
            {status?.last_sync ? (
              <>
              <dl className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <Field2 label="Estado" value={syncStatusLabel(status.last_sync, status.running)} />
                <Field2 label="Inicio" value={fmtDate(status.last_sync.started_at)} />
                <Field2 label="Duración" value={`${status.last_sync.duration_seconds ?? 0}s`} />
                <Field2 label="Total" value={status.last_sync.items_total} />
                <Field2 label="Nuevos" value={status.last_sync.items_new} />
                <Field2 label="Actualizados" value={status.last_sync.items_updated} />
                <Field2 label="Archivados" value={status.last_sync.items_archived} />
                <Field2 label="Errores" value={(status.last_sync.errors || []).length} />
              </dl>
              {(status.last_sync.errors || []).length > 0 && (
                <ul className="mt-5 text-xs text-red-700 space-y-1 break-words" data-testid="admin-sync-errors">
                  {status.last_sync.errors.slice(0, 5).map((e, i) => (<li key={i}>{e}</li>))}
                </ul>
              )}
              </>
            ) : (
              <p className="text-muted-foreground text-sm">Aún no hay sync registrado.</p>
            )}
          </div>
        </div>
      )}

      {tab === "subscribers" && (
        <div data-testid="admin-subscribers-tab">
          <div className="flex justify-end mb-3">
            <button onClick={() => exportCSV("subscribers")} className="vmr-tiny border border-border px-3 py-1.5 inline-flex items-center gap-1.5 hover:border-foreground">
              <Download size={12} /> Exportar CSV
            </button>
          </div>
          <div className="border border-border">
          <table className="w-full text-sm">
            <thead className="bg-[hsl(var(--muted))] vmr-tiny text-muted-foreground">
              <tr>
                <th className="text-left p-3">Email</th>
                <th className="text-left p-3">Plan</th>
                <th className="text-left p-3">Activo</th>
                <th className="text-left p-3">Fecha alta</th>
              </tr>
            </thead>
            <tbody>
              {subscribers.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">Sin suscriptores aún</td></tr>
              )}
              {subscribers.map((s) => (
                <tr key={s.email} className="border-t border-border">
                  <td className="p-3">{s.email}</td>
                  <td className="p-3"><span className="vmr-tiny">{s.tier}</span></td>
                  <td className="p-3">{s.active ? "Sí" : "No"}</td>
                  <td className="p-3 text-muted-foreground">{fmtDate(s.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {tab === "campaigns" && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-10" data-testid="admin-campaigns-tab">
          <div>
            <h2 className="text-2xl mb-6">Nueva campaña</h2>
            <div className="flex flex-col gap-4">
              <input
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Asunto"
                className="bg-transparent border border-border px-3 py-3"
                data-testid="campaign-subject"
              />
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={10}
                placeholder="Cuerpo en markdown… (#, ##, **bold**, *italic*, [link](url), - lista)"
                className="bg-transparent border border-border px-3 py-3 font-mono text-sm"
                data-testid="campaign-body"
              />
              <div className="flex items-center gap-4 vmr-tiny">
                <span className="text-muted-foreground">Tiers:</span>
                {Object.keys(tiers).map((k) => (
                  <label key={k} className="flex items-center gap-1.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={tiers[k]}
                      onChange={(e) => setTiers({ ...tiers, [k]: e.target.checked })}
                      className="w-3.5 h-3.5 accent-[hsl(var(--accent))]"
                      data-testid={`campaign-tier-${k}`}
                    />
                    {k}
                  </label>
                ))}
              </div>
              <button
                onClick={sendCampaign}
                className="self-start bg-[hsl(var(--accent))] text-white px-6 py-3 vmr-tiny inline-flex items-center gap-2"
                data-testid="campaign-send"
              >
                <Send size={12} /> Enviar campaña
              </button>
            </div>
          </div>
          <div>
            <h2 className="text-2xl mb-6">Historial</h2>
            <div className="space-y-4">
              {campaigns.length === 0 && <p className="text-muted-foreground text-sm">Sin campañas todavía.</p>}
              {campaigns.map((c) => (
                <div key={c.id} className="border border-border p-4">
                  <p className="font-medium">{c.subject}</p>
                  <p className="vmr-tiny text-muted-foreground mt-1">
                    {fmtDate(c.created_at)} · {c.stats ? `${c.stats.sent}/${c.stats.total} enviados` : "Enviando…"}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === "content" && (
        <div data-testid="admin-content-tab">
          <div className="mb-8 border-b border-border pb-4">
            <h2 className="text-2xl">Editor de textos</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Textos, fotos y datos de la web, ordenados por sección. Pulsa «Guardar» en cada bloque; el cambio sale en la web al recargar.
            </p>
          </div>
          {!content ? (
            <p className="text-muted-foreground text-sm">Cargando…</p>
          ) : (
            <div className="flex flex-col gap-12">
              {groupKeys(content.keys).map((section) => (
                <section key={section.title} className="flex flex-col gap-5" data-testid={`content-section-${section.title}`}>
                  <h3 className="text-lg font-medium border-b border-border pb-2">{section.title}</h3>
                  {section.items.map(({ key, meta }) => (
                    <ContentEditor
                      key={key}
                      blockKey={key}
                      meta={meta}
                      current={content.blocks[key] || content.defaults[key]}
                      defaults={content.defaults[key]}
                      onSave={saveBlock}
                      onReset={resetBlock}
                      client={client}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}
        </div>
      )}
      {tab === "clicks" && (
        <div data-testid="admin-clicks-tab">
          <div className="mb-8 border-b border-border pb-4">
            <h2 className="text-2xl">Clicks a Discogs</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Top discos por número de clicks en el botón "Comprar en Discogs". Te sirve para ver qué se mira más.
            </p>
            {clicks && (
              <p className="vmr-tiny text-muted-foreground mt-3">Total clicks acumulados: {clicks.total}</p>
            )}
          </div>
          {!clicks || clicks.items.length === 0 ? (
            <p className="text-muted-foreground text-sm">Aún no hay clicks registrados.</p>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[hsl(var(--muted))] vmr-tiny text-muted-foreground">
                <tr>
                  <th className="text-left p-3">#</th>
                  <th className="text-left p-3">Disco</th>
                  <th className="text-right p-3">Precio</th>
                  <th className="text-right p-3">Clicks</th>
                  <th className="text-left p-3">Último</th>
                  <th className="p-3"></th>
                </tr>
              </thead>
              <tbody>
                {clicks.items.map((row, i) => (
                  <tr key={row.listing.slug} className="border-t border-border">
                    <td className="p-3 text-muted-foreground tabular-nums">{i + 1}</td>
                    <td className="p-3">
                      <div className="flex items-center gap-3">
                        {row.listing.thumb && (
                          <img src={row.listing.thumb} alt="" className="w-10 h-10 object-cover" />
                        )}
                        <div>
                          <div className="text-xs text-muted-foreground">{row.listing.artist}</div>
                          <div className="font-medium">{row.listing.title}</div>
                        </div>
                      </div>
                    </td>
                    <td className="p-3 text-right tabular-nums">
                      {row.listing.price ? `${row.listing.price.toFixed(2)} €` : "—"}
                    </td>
                    <td className="p-3 text-right font-medium tabular-nums">{row.clicks}</td>
                    <td className="p-3 text-muted-foreground vmr-tiny">{fmtDate(row.last)}</td>
                    <td className="p-3">
                      <a
                        href={row.listing.discogs_uri}
                        target="_blank"
                        rel="noreferrer"
                        className="vmr-tiny text-[hsl(var(--accent))] hover:underline"
                      >
                        Ver →
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
      {tab === "orders" && (
        <AdminOrders orders={orders} client={client} paymentText={cms.blocks?.["order.payment"]?.es || ""} onChanged={loadOrders} />
      )}

      {tab === "articles" && <AdminArticles client={client} />}

      {tab === "sell" && (
        <AdminSellRequests data={sellRequests} client={client} onChanged={loadSellRequests} />
      )}

      {tab === "messages" && (
        <div data-testid="admin-messages-tab">
          <div className="mb-6 border-b border-border pb-4">
            <h2 className="text-2xl">Mensajes de contacto</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Mensajes recibidos a través del formulario en /contacto.
            </p>
          </div>
          {(!messages || messages.items.length === 0) ? (
            <p className="text-muted-foreground text-sm">Sin mensajes todavía.</p>
          ) : (
            <div className="space-y-3">
              {messages.items.map((m) => (
                <div key={m.id} className="border border-border p-4">
                  <div className="flex items-start justify-between gap-4 mb-2">
                    <div>
                      <p className="text-sm font-medium">{m.name}</p>
                      <a href={gmailCompose({ to: m.email })} target="_blank" rel="noreferrer" className="vmr-tiny text-muted-foreground hover:text-foreground underline underline-offset-4">{m.email}</a>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="vmr-tiny text-muted-foreground">{fmtDate(m.created_at)}</span>
                      <button onClick={() => deleteMessage(m.id)} className="text-muted-foreground hover:text-[hsl(var(--accent))]" title="Eliminar">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                  <p className="text-sm leading-relaxed whitespace-pre-line">{m.message}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {tab === "catalog" && (
        <div data-testid="admin-catalog-tab">
          <div className="mb-6 border-b border-border pb-4">
            <h2 className="text-2xl">Catálogo</h2>
            <p className="text-sm text-muted-foreground mt-1">
              Buscar, ocultar manualmente discos del catálogo (ej. si lo vendiste fuera de la web) o restaurarlos. Los marcados como "vendidos web" no aparecen al cliente.
            </p>
          </div>

          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <form onSubmit={(e) => { e.preventDefault(); setListingsPage(1); loadListings(listingsQ, listingsStatus, 1); }} className="flex items-center gap-2 flex-1 min-w-[300px]">
              <Search size={14} className="text-muted-foreground" />
              <input
                type="text" value={listingsQ} onChange={(e) => setListingsQ(e.target.value)}
                placeholder="Artista, título, sello, ref…"
                className="flex-1 bg-transparent border border-border px-3 py-2 text-sm focus:outline-none focus:border-foreground"
              />
              <button type="submit" className="bg-foreground text-background px-3 py-2 vmr-tiny">Buscar</button>
            </form>
            <div className="flex gap-1.5">
              {[
                { k: "active", l: "Activos" },
                { k: "sold", l: "Vendidos web" },
                { k: "archived", l: "Archivados" },
              ].map((s) => (
                <button
                  key={s.k}
                  onClick={() => { setListingsStatus(s.k); setListingsPage(1); }}
                  className={`vmr-tiny px-3 py-1.5 border ${listingsStatus === s.k ? "bg-foreground text-background border-foreground" : "border-border hover:border-foreground"}`}
                >
                  {s.l}
                </button>
              ))}
            </div>
          </div>

          {!listings ? (
            <p className="text-sm text-muted-foreground">Cargando…</p>
          ) : listings.items.length === 0 ? (
            <p className="text-sm text-muted-foreground">Sin resultados.</p>
          ) : (
            <>
              <div className="vmr-tiny text-muted-foreground mb-2">{listings.total} resultados · página {listings.page} de {listings.pages}</div>
              <div className="border border-border">
                <table className="w-full text-sm">
                  <thead className="bg-[hsl(var(--muted))] vmr-tiny text-muted-foreground">
                    <tr>
                      <th className="text-left p-3"></th>
                      <th className="text-left p-3">Disco</th>
                      <th className="text-left p-3">Ref</th>
                      <th className="text-right p-3">Precio</th>
                      <th className="text-left p-3">Estado</th>
                      <th className="p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {listings.items.map((l) => (
                      <tr key={l.listing_id} className="border-t border-border">
                        <td className="p-2">
                          {l.thumb && <img src={l.thumb} alt="" className="w-10 h-10 object-cover" />}
                        </td>
                        <td className="p-3">
                          <div className="text-xs text-muted-foreground">{l.artist}</div>
                          <div>{l.title}</div>
                        </td>
                        <td className="p-3 vmr-tiny text-muted-foreground">
                          {[l.label, l.catno].filter(Boolean).join(" · ") || "—"}
                        </td>
                        <td className="p-3 text-right tabular-nums">{(l.price || 0).toFixed(2)} €</td>
                        <td className="p-3">
                          {l.archived ? (
                            <span className="vmr-tiny uppercase text-muted-foreground">archivado</span>
                          ) : l.web_sold ? (
                            <span className="vmr-tiny uppercase text-[hsl(var(--accent))]">vendido web</span>
                          ) : (
                            <span className="vmr-tiny uppercase text-foreground">activo</span>
                          )}
                        </td>
                        <td className="p-3 text-right">
                          {!l.archived && (
                            <button
                              onClick={() => toggleListing(l.listing_id, !l.web_sold)}
                              className="vmr-tiny inline-flex items-center gap-1 hover:text-[hsl(var(--accent))]"
                              title={l.web_sold ? "Restaurar (mostrar de nuevo)" : "Ocultar del catálogo"}
                            >
                              {l.web_sold ? <><Eye size={12} /> Restaurar</> : <><EyeOff size={12} /> Ocultar</>}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="flex items-center justify-between mt-4 vmr-tiny">
                <button
                  disabled={listings.page <= 1}
                  onClick={() => setListingsPage((p) => p - 1)}
                  className="disabled:opacity-30 hover:text-foreground text-muted-foreground"
                >← Anterior</button>
                <span className="text-muted-foreground">{listings.page} / {listings.pages}</span>
                <button
                  disabled={listings.page >= listings.pages}
                  onClick={() => setListingsPage((p) => p + 1)}
                  className="disabled:opacity-30 hover:text-foreground text-muted-foreground"
                >Siguiente →</button>
              </div>
            </>
          )}
        </div>
      )}

    </div>
  );
}

function BlockHeader({ blockKey, meta, onReset, note }) {
  return (
    <div className="flex items-start justify-between gap-4 mb-4">
      <div>
        <p className="text-sm font-medium">{meta?.label || blockKey}</p>
        {(meta?.help || note) && <p className="text-xs text-muted-foreground mt-1">{meta?.help || note}</p>}
        <code className="text-[10px] text-muted-foreground/70">{blockKey}</code>
      </div>
      <button
        onClick={() => onReset(blockKey)}
        className="vmr-tiny text-muted-foreground hover:text-foreground inline-flex items-center gap-1 shrink-0"
        data-testid={`content-reset-${blockKey}`}
      >
        <RotateCcw size={11} /> Restaurar
      </button>
    </div>
  );
}

async function uploadImage(client, file) {
  const formData = new FormData();
  formData.append("file", file);
  const { data } = await client.post("/admin/upload", formData, { headers: { "Content-Type": "multipart/form-data" } });
  return data.url;
}

/** Several photos stored one URL per line (e.g. the gallery on "Sobre"). */
function GalleryEditor({ blockKey, meta, current, onSave, onReset, client }) {
  const toList = (v) => (v || "").split("\n").map((u) => u.trim()).filter(Boolean);
  const [urls, setUrls] = useState(toList(current?.es));
  const [uploading, setUploading] = useState(false);
  useEffect(() => setUrls(toList(current?.es)), [current]);

  const add = async (e) => {
    const files = [...(e.target.files || [])];
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) uploaded.push(await uploadImage(client, f));
      setUrls((u) => [...u, ...uploaded]);
      toast.success(`${uploaded.length} ${uploaded.length === 1 ? "foto subida" : "fotos subidas"}. Pulsa Guardar.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al subir la foto");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  const move = (i, d) => setUrls((u) => {
    const j = i + d;
    if (j < 0 || j >= u.length) return u;
    const c = [...u];
    [c[i], c[j]] = [c[j], c[i]];
    return c;
  });
  const changed = urls.join("\n") !== toList(current?.es).join("\n");

  return (
    <div className="border border-border p-5 md:p-6" data-testid={`content-block-${blockKey}`}>
      <BlockHeader blockKey={blockKey} meta={meta} onReset={onReset} />
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
        {urls.map((u, i) => (
          <div key={u + i} className="relative group">
            <img src={u} alt="" className="aspect-square w-full object-cover bg-[hsl(var(--muted))] border border-border" />
            <div className="absolute inset-x-0 bottom-0 flex justify-between bg-background/90 text-xs">
              <button onClick={() => move(i, -1)} className="px-2 py-1 hover:text-[hsl(var(--accent))]" aria-label="Mover a la izquierda">←</button>
              <button onClick={() => setUrls((x) => x.filter((_, j) => j !== i))} className="px-2 py-1 hover:text-red-700" aria-label="Quitar foto">Quitar</button>
              <button onClick={() => move(i, 1)} className="px-2 py-1 hover:text-[hsl(var(--accent))]" aria-label="Mover a la derecha">→</button>
            </div>
          </div>
        ))}
        <label className="aspect-square border border-dashed border-border grid place-items-center text-center cursor-pointer hover:border-foreground hover:bg-[hsl(var(--muted))] text-xs p-2" data-testid={`content-gallery-add-${blockKey}`}>
          <input type="file" accept="image/*" multiple className="hidden" onChange={add} disabled={uploading} />
          {uploading ? "Subiendo…" : "+ Añadir fotos"}
        </label>
      </div>
      <div className="mt-4 flex items-center justify-between">
        <span className="vmr-tiny text-muted-foreground">{urls.length} {urls.length === 1 ? "foto" : "fotos"}{changed ? " · sin guardar" : ""}</span>
        <button
          onClick={() => onSave(blockKey, urls.join("\n"), "")}
          className="bg-foreground text-background px-4 py-2 vmr-tiny inline-flex items-center gap-2 hover:bg-[hsl(var(--accent))] transition-colors"
          data-testid={`content-save-${blockKey}`}
        >
          <Save size={12} /> Guardar
        </button>
      </div>
    </div>
  );
}

function ContentEditor({ blockKey, meta, current, defaults, onSave, onReset, client }) {
  if (blockKey.startsWith("gallery.")) {
    return <GalleryEditor blockKey={blockKey} meta={meta} current={current} onSave={onSave} onReset={onReset} client={client} />;
  }
  return <TextOrImageEditor blockKey={blockKey} meta={meta} current={current} defaults={defaults} onSave={onSave} onReset={onReset} client={client} />;
}

function TextOrImageEditor({ blockKey, meta, current, defaults, onSave, onReset, client }) {
  const [es, setEs] = useState(current?.es || "");
  const [en, setEn] = useState(current?.en || "");
  const [uploading, setUploading] = useState(false);
  useEffect(() => {
    setEs(current?.es || "");
    setEn(current?.en || "");
  }, [current]);

  const isImage = blockKey.startsWith("image.");
  const isMultiline = !isImage && (meta?.multiline || (current?.es || "").length > 90 || (current?.es || "").includes("\n"));
  const Input = isMultiline ? "textarea" : "input";
  const inputProps = isMultiline ? { rows: meta?.single ? 3 : 6 } : {};
  const isDefault = es === defaults?.es && en === defaults?.en;

  const resolveImg = (url) => {
    if (!url) return null;
    if (url.startsWith("http")) return url;
    return `${process.env.REACT_APP_BACKEND_URL || ""}${url}`;
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await client.post("/admin/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEs(data.url);
      setEn(data.url);
      toast.success("Imagen subida");
    } catch (err) {
      toast.error("Error al subir la imagen");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  if (isImage) {
    return (
      <div className="border border-border p-5 md:p-6" data-testid={`content-block-${blockKey}`}>
        <BlockHeader blockKey={blockKey} meta={meta} onReset={onReset} note="Sube un archivo o pega una dirección (URL) de imagen." />
        <div className="grid grid-cols-1 md:grid-cols-[200px_1fr] gap-5 items-start">
          <div className="aspect-square bg-[hsl(var(--muted))] overflow-hidden border border-border">
            {es ? (
              <img src={resolveImg(es)} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center text-xs text-muted-foreground">Sin imagen</div>
            )}
          </div>
          <div className="flex flex-col gap-3">
            <input
              type="text"
              value={es}
              onChange={(e) => { setEs(e.target.value); setEn(e.target.value); }}
              placeholder="https://… o /api/uploads/…"
              className="bg-transparent border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
              data-testid={`content-image-url-${blockKey}`}
            />
            <label
              className="border border-dashed border-border px-4 py-6 text-center cursor-pointer hover:border-foreground hover:bg-[hsl(var(--muted))] transition-colors"
              data-testid={`content-upload-${blockKey}`}
            >
              <input type="file" accept="image/*" className="hidden" onChange={handleUpload} disabled={uploading} />
              <span className="vmr-tiny">
                {uploading ? "Subiendo…" : "Subir archivo (jpg, png, webp, máx 10 MB)"}
              </span>
            </label>
            <div className="flex items-center justify-end gap-3">
              <span className="vmr-tiny text-muted-foreground">{isDefault ? "Sin cambios" : "Modificado"}</span>
              <button
                onClick={() => onSave(blockKey, es, en)}
                className="bg-foreground text-background px-4 py-2 vmr-tiny inline-flex items-center gap-2 hover:bg-[hsl(var(--accent))] transition-colors"
                data-testid={`content-save-${blockKey}`}
              >
                <Save size={12} /> Guardar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border p-5 md:p-6" data-testid={`content-block-${blockKey}`}>
      <BlockHeader blockKey={blockKey} meta={meta} onReset={onReset} />
      {meta?.single ? (
        <Input
          {...inputProps}
          type={isMultiline ? undefined : "text"}
          value={es}
          onChange={(e) => { setEs(e.target.value); setEn(""); }}
          className="w-full bg-transparent border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
          data-testid={`content-es-${blockKey}`}
        />
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="flex flex-col gap-1.5">
          <label className="vmr-tiny text-muted-foreground">Español</label>
          <Input
            {...inputProps}
            type={isMultiline ? undefined : "text"}
            value={es}
            onChange={(e) => setEs(e.target.value)}
            className="bg-transparent border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
            data-testid={`content-es-${blockKey}`}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="vmr-tiny text-muted-foreground">English</label>
          <Input
            {...inputProps}
            type={isMultiline ? undefined : "text"}
            value={en}
            onChange={(e) => setEn(e.target.value)}
            className="bg-transparent border border-border px-3 py-2.5 text-sm focus:outline-none focus:border-foreground"
            data-testid={`content-en-${blockKey}`}
          />
        </div>
      </div>
      )}
      <div className="mt-4 flex items-center justify-between">
        <span className="vmr-tiny text-muted-foreground">{isDefault ? "Sin cambios respecto al texto por defecto" : "Modificado"}</span>
        <button
          onClick={() => onSave(blockKey, es, en)}
          className="bg-foreground text-background px-4 py-2 vmr-tiny inline-flex items-center gap-2 hover:bg-[hsl(var(--accent))] transition-colors"
          data-testid={`content-save-${blockKey}`}
        >
          <Save size={12} /> Guardar
        </button>
      </div>
    </div>
  );
}

function Card({ label, value, sub }) {
  return (
    <div className="border border-border p-5">
      <p className="vmr-tiny text-muted-foreground mb-2">{label}</p>
      <p className="text-2xl md:text-3xl tabular-nums">{value}</p>
      {sub && <p className="vmr-tiny text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}
function syncStatusLabel(last, running) {
  if (last.status === "success") return "Correcto";
  if (last.status === "failed") return "Fallido";
  if (last.status === "running") return running ? "En curso" : "Interrumpido";
  return last.status;
}

function Field2({ label, value }) {
  return (
    <div>
      <dt className="vmr-tiny text-muted-foreground mb-1">{label}</dt>
      <dd className="text-sm font-medium">{value ?? "—"}</dd>
    </div>
  );
}
function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES");
  } catch {
    return iso;
  }
}
